// Utility functions for calculating distances between coordinates

export interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Calculate the distance between two points using the Haversine formula
 * @param coord1 First coordinate point
 * @param coord2 Second coordinate point
 * @returns Distance in miles
 */
export function calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRadians(coord2.lat - coord1.lat);
  const dLng = toRadians(coord2.lng - coord1.lng);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(coord1.lat)) * Math.cos(toRadians(coord2.lat)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Extract coordinates from Google Places result
 */
export function extractCoordinates(place: google.maps.places.PlaceResult): Coordinates | null {
  if (!place.geometry?.location) return null;
  
  const lat = typeof place.geometry.location.lat === 'function' 
    ? place.geometry.location.lat() 
    : (place.geometry.location.lat as unknown as number);
  const lng = typeof place.geometry.location.lng === 'function' 
    ? place.geometry.location.lng() 
    : (place.geometry.location.lng as unknown as number);
    
  return { lat, lng };
}

/**
 * Validate that an address is within the maximum distance from a location
 */
export function validateAddressDistance(
  locationCoords: Coordinates | null,
  addressCoords: Coordinates | null,
  maxDistanceMiles: number = 5
): { isValid: boolean; distance?: number; error?: string } {
  if (!locationCoords) {
    return { isValid: false, error: "Location coordinates not available" };
  }
  
  if (!addressCoords) {
    return { isValid: false, error: "Address coordinates not available" };
  }
  
  const distance = calculateDistance(locationCoords, addressCoords);
  
  if (distance > maxDistanceMiles) {
    return { 
      isValid: false, 
      distance, 
      error: `Address is ${distance.toFixed(1)} miles from the location. Please choose an address within ${maxDistanceMiles} miles.` 
    };
  }
  
  return { isValid: true, distance };
}